import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'

import { default as authPaymentImg, default as valuePaymentImg } from '../../assets/images/ico-wifi.png'

import { make3CIPageKioskMobileSessionPaymentEndpoint } from './api'

import Loading from '../../components/Loading'
import { Portal } from '../../components/Portal'
import useAxiosFetch from '../../utils/useAxiosFetch'

function usePayments() {
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [paymentRes, setPaymentRes] = useState(null)
  const [paymentError, setPaymentError] = useState(null)

  const { loading, error, data } = useAxiosFetch('/session-payments')

  const paymentValues = useMemo(() => {
    if (!data) return null
    return {
      skipPayments: !data.paymentEnabled && !data.authEnabled,
      valuePaymentEnabled: data.paymentEnabled,
      valuePayment: data.paymentAmount,
      valueDepositPaymentEnabled: data.authEnabled,
      valueDepositPayment: data.authAmount,
    }
    return null
  }, [data])

  const handlePaymentClick = useCallback(() => {
    if (!loadingPayment) {
      setLoadingPayment(true)
      make3CIPageKioskMobileSessionPaymentEndpoint()
        .then((res) => {
          setPaymentRes(res)
          setLoadingPayment(false)
        })
        .catch((err) => {
          setPaymentError(err)
          setLoadingPayment(false)
        })
    }
  }, [loadingPayment])

  return {
    isLoading: loading || loadingPayment,
    error: error || paymentError,
    paymentValues,
    handlePaymentClick,
    paymentRes,
  }
}

export function PaymentsForm({ nextPageUrl = '/' }) {
  const { t } = useTranslation()
  const { isLoading, error, paymentValues, handlePaymentClick, paymentRes } = usePayments()

  if (isLoading) {
    return <Loading active={true} message={t('mobilePaymentsLoading')} />
  }

  if (error) {
    return <Redirect to="/payments-error" push={true} />
  }

  if (paymentValues?.skipPayments) {
    return <Redirect to={nextPageUrl} />
  }

  return paymentValues ? (
    <div>
      <div className="text-center mt-4 color-green">
        <div className="text-uppercase">{t('mobilePaymentsTitle')}</div>
        <div className="">{t('mobilePaymentsSubTitle')}</div>
      </div>
      <div className="mt-4 mb-4">
        <PaymentLine
          enabled={paymentValues.valuePaymentEnabled}
          value={paymentValues.valuePayment}
          icon={valuePaymentImg}
          title={t('mobilePaymentsValuePayment')}
          message={t('mobilePaymentsValuePaymentMessage')}
        />
        <PaymentLine
          enabled={paymentValues.valueDepositPaymentEnabled}
          value={paymentValues.valueDepositPayment}
          icon={authPaymentImg}
          title={t('mobilePaymentsAuthPayment')}
          message={t('mobilePaymentsAuthPaymentMessage')}
        />
      </div>
      <div onClick={handlePaymentClick} className="btn btn-orange cursor" id="payment-btn">
        {t('mobilePaymentsSubmit')}
      </div>
      <Supplier3CiPageForm paymentRes={paymentRes || {}} paymentValues={paymentValues} />
    </div>
  ) : null
}

function PaymentLine({ enabled, value, icon, title, message }) {
  return enabled ? (
    <div className="payment">
      <div className="payment__icon">
        <img src={icon} alt="" />
      </div>
      <div className="payment__text">
        <div className="payment__title">{title}</div>
        <div className="payment__message">{message}</div>
      </div>
      <div className="payment__value">{value} €</div>
    </div>
  ) : null
}

function Supplier3CiPageForm({ paymentRes, paymentValues }) {
  const formRef = useRef(null)

  useEffect(() => {
    if (paymentRes?.redirectURL) {
      formRef.current.submit()
    }
  }, [paymentRes])

  return paymentRes ? (
    <>
      <form ref={formRef} id="formSubmit" method="post" target="btnIFrameIPage" action={paymentRes.redirectURL}>
        <input type="hidden" id="txtipgSessionValues" name="XXX_IPGSESSION_XXX" value={paymentRes.ipgSession} />
        <input
          type="hidden"
          name="merchant_script_data_1"
          value={
            paymentValues.valuePaymentEnabled && paymentValues.valueDepositPaymentEnabled
              ? paymentValues.valuePayment
              : ''
          }
        ></input>
      </form>
      {paymentRes.redirectURL ? (
        <Portal id="pay3CiPage">
          <iframe title="btnIFrameIPage" id="btnIFrameIPage" name="btnIFrameIPage" frameBorder={0}></iframe>
        </Portal>
      ) : null}
    </>
  ) : null
}
